// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { ReduxStore } from '@makemydeal/dr-shared-store';

// actions
import { releaseProgramFallbackValues } from '../../actions/programOrchestrationActionCreators';

// selectors
import { isProgramLookupPending } from '../../selectors/programsSelectors';

/**
 * A rate or residual override that stays on program-quotes carries this, and `programInvalidationMiddleware`
 * settles it from that override's OWN response: a `PAYMENT_RECEIVED` releases the values held for a fallback, a
 * `PAYMENT_FAILURE` falls back to user-program-quotes.
 *
 * Shared because the payment sequence guard is the one other place that has to know: when it discards a superseded
 * override's `PAYMENT_RECEIVED` (DE784527), the release that response would have triggered has to happen anyway.
 */
export const PQ_OVERRIDE_RECOVERY_META = 'pqOverrideRecovery';

export const isPqOverrideRecoveryOutcome = (action: AnyFSA): boolean =>
    Boolean(action.meta?.originalAction?.meta?.[PQ_OVERRIDE_RECOVERY_META]);

export const FINANCE_RATE_FALLBACK_META = 'financeRateFallback';

export const isFinanceRateFallbackOutcome = (action: AnyFSA, offerType: OfferType): boolean =>
    offerType === FINANCE && Boolean(action.meta.originalAction.meta[FINANCE_RATE_FALLBACK_META]);

/** A Finance recovery made while a program lookup is pending leaves the held values to that lookup. */
export const doesLookupHoldRecoveryValues = (state: StateTree, action: AnyFSA, offerType: OfferType): boolean =>
    isFinanceRateFallbackOutcome(action, offerType) && isProgramLookupPending(state, offerType);

/** What a PQ override recovery's `PAYMENT_RECEIVED` does to the held values, wherever that response ends up. */
export const releasePqOverrideRecoveryHold = (
    store: ReduxStore<StateTree>,
    state: StateTree,
    action: AnyFSA,
    offerType: OfferType
) => {
    if (!doesLookupHoldRecoveryValues(state, action, offerType)) releaseProgramFallbackValues(store);
};
